import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Search, AlertTriangle, Clock3, UtensilsCrossed, ArrowRight, Star, ReceiptText, BellRing, Soup } from "lucide-react";
import api, { apiErrorMessage, fileUrl, formatIDR } from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageState } from "@/components/ui/page-state";
import CartDrawer from "@/components/order/cart-drawer";
import ItemDrawer from "@/components/menu/item-detail";
import { StorefrontHeader, StorefrontHero, OutletServiceSheet } from "@/components/menu/order-storefront";
import { STITCH_DINING_IMAGE } from "@/lib/stitch-menu-assets";
import { MenuCard, FeaturedMenuCard } from "@/components/menu/menu-card";

export default function OrderPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderType = params.get("type") === "room" ? "room" : "dinein";
  const tableNumberParam = params.get("tableNumber") || "";
  const outletSlug = params.get("outlet") || "";
  const mode = params.get("mode") || "dinein";
  const categoryFromQuery = params.get("category") || "";
  const { loadOutletBySlug } = useOutlet();
  const [outlet, setOutlet] = useState(null);
  const [outletError, setOutletError] = useState("");

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [showService, setShowService] = useState(false);
  const searchRef = useRef(null);
  const featuredRef = useRef(null);
  const menuRef = useRef(null);
  const [cart, setCart] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCart, setShowCart] = useState(false);
  // QR Room Service bisa memakai nomor placeholder (mis. 0) — jangan ikut terisi,
  // tamu wajib mengetik sendiri nomor kamarnya di keranjang.
  const roomNumberFromUrl =
    orderType === "room" && /^\d+$/.test(tableNumberParam) && Number(tableNumberParam) > 0 ? tableNumberParam : "";
  const [roomNumber, setRoomNumber] = useState(roomNumberFromUrl);
  const [roomNumberError, setRoomNumberError] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerPhoneError, setCustomerPhoneError] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [showOutletPopup, setShowOutletPopup] = useState(false);
  const [showPromotionPopup, setShowPromotionPopup] = useState(false);
  const [voucherInput, setVoucherInput] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  // Menu sudah pernah dimuat? Dipakai saat outlet beralih dari tutup ke buka (polling status).
  const menuLoadedRef = useRef(false);
  const menuRequestRef = useRef(0);
  const outletRequestRef = useRef(0);
  const outletSlugRef = useRef(outletSlug);
  outletSlugRef.current = outletSlug;

  const fetchMenu = async (outletId) => {
    if (!outletId) return false;
    const requestId = ++menuRequestRef.current;
    setMenuLoading(true);
    setMenuError("");
    try {
      const [c, m] = await Promise.all([
        api.get("/categories"),
        api.get("/menu-items", { params: { outlet_id: outletId } }),
      ]);
      if (requestId !== menuRequestRef.current) return false;
      setCategories(c.data);
      const selectedCategory = resolveCategoryParam(c.data || [], categoryFromQuery);
      if (selectedCategory) setActiveCat(selectedCategory);
      setItems(m.data);
      menuLoadedRef.current = true;
      return true;
    } catch {
      if (requestId !== menuRequestRef.current) return false;
      setMenuError("Menu belum dapat dimuat. Periksa koneksi Anda lalu coba lagi.");
      toast.error("Gagal memuat menu");
      return false;
    } finally {
      if (requestId === menuRequestRef.current) setMenuLoading(false);
    }
  };

  // Status buka/tutup mengikuti jam buka outlet (`hours`) — dicek ulang tiap menit agar
  // halaman beralih sendiri tanpa reload tepat saat jam buka/tutup tiba.
  useEffect(() => {
    if (!outletSlug || !outlet?.slug) return undefined;
    const syncOpenState = async () => {
      try {
        const { data } = await api.get(`/outlets/by-slug/${outletSlug}`);
        if (outletSlugRef.current !== outletSlug) return;
        setOutlet((cur) => (cur ? {
          ...cur,
          is_open_now: data.is_open_now,
          open_auto: data.open_auto,
          open_reason: data.open_reason,
          opens_at: data.opens_at,
          closes_at: data.closes_at,
        } : cur));
        if (!isOutletClosed(data) && !menuLoadedRef.current) await fetchMenu(data.id);
      } catch { /* jaringan bermasalah: pertahankan status terakhir */ }
    };
    const timer = setInterval(syncOpenState, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletSlug, outlet?.slug]);

  useEffect(() => {
    (async () => {
      const requestId = ++outletRequestRef.current;
      menuRequestRef.current += 1;
      menuLoadedRef.current = false;
      setItems([]);
      setCategories([]);
      setMenuLoading(true);
      setMenuError("");
      setOutletError("");
      if (!outletSlug) {
        setOutletError("Outlet tidak ditentukan. Silakan scan QR meja terlebih dahulu.");
        return;
      }
      const o = await loadOutletBySlug(outletSlug);
      if (requestId !== outletRequestRef.current || outletSlugRef.current !== outletSlug) return;
      if (!o) {
        setOutletError(`Outlet "${outletSlug}" tidak ditemukan atau tidak aktif.`);
        return;
      }
       setOutlet(o);
       const popupMessage = (o.popup_message || "").trim();
       const popupVersion = `${o.updated_at || ""}:${popupMessage}`;
       const popupSeenKey = `resto_popup_seen_${o.slug}_${popupVersion}`;
       if (o.popup_enabled && popupMessage && localStorage.getItem(popupSeenKey) !== "1") {
         setShowOutletPopup(true);
       }
       const promotionImage = (o.promotion_image_url || o.promotion_image_file_id) ? (
         o.promotion_image_file_id ? fileUrl(o.promotion_image_file_id) : o.promotion_image_url
       ) : "";
       const promotionVersion = `${o.updated_at || ""}:${promotionImage}`;
       const promotionSeenKey = `resto_promotion_seen_${o.slug}_${promotionVersion}`;
       if (o.promotion_enabled && promotionImage && localStorage.getItem(promotionSeenKey) !== "1") {
         setShowPromotionPopup(true);
       }
      if (isOutletClosed(o)) return;
      await fetchMenu(o.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletSlug]);

  const headerSrc = outlet?.header_image_file_id ? fileUrl(outlet.header_image_file_id)
    : outlet?.header_image_url || STITCH_DINING_IMAGE;
  const featuredCategory = categories.find((category) => /kraton|keraton/i.test(category.name) && items.some((item) => item.category_id === category.id && item.available !== false));
  const featuredItems = items.filter((item) => item.available !== false && (!featuredCategory || item.category_id === featuredCategory.id)).slice(0, 4);
  const showFeatured = !menuLoading && !menuError && activeCat === "all" && !search && !availableOnly && featuredItems.length > 0;
  const focusSearch = () => {
    searchRef.current?.scrollIntoView?.({ block: "center" });
    searchRef.current?.focus({ preventScroll: true });
  };
  const showAllMenu = () => {
    setActiveCat("all"); setSearch(""); setAvailableOnly(false);
    menuRef.current?.scrollIntoView?.({ block: "start" });
    menuRef.current?.focus({ preventScroll: true });
  };
  const showFeaturedMenu = () => {
    setActiveCat("all"); setSearch(""); setAvailableOnly(false);
    requestAnimationFrame(() => (featuredRef.current || menuRef.current)?.scrollIntoView?.({ block: "start" }));
  };

  const filteredItems = useMemo(() => {
    let list = availableOnly ? items.filter((item) => item.available !== false) : items;
    if (activeCat !== "all") list = list.filter((i) => i.category_id === activeCat);
    if (search) list = list.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
    // Available items first, sold out at the bottom (stable)
    return [...list].sort((a, b) => (b.available !== false) - (a.available !== false));
  }, [items, activeCat, search, availableOnly]);

  const addToCart = (item, qty = 1, notes = "") => {
    if (item.available === false) return toast.error("Menu sedang tidak tersedia");
    setCart((prev) => {
      const existing = prev[item.id];
      const nextQty = (existing?.qty || 0) + qty;
      if (nextQty <= 0) {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [item.id]: { item, qty: nextQty, notes: notes || existing?.notes || "" } };
    });
    if (qty > 0) toast.success(`${item.name} ditambahkan`);
  };

  const removeFromCart = (id) => setCart((prev) => { const { [id]: _, ...rest } = prev; return rest; });

  const cartArray = Object.values(cart);
  const cartCount = cartArray.reduce((s, c) => s + c.qty, 0);

  // Related menu: same category as cart items first, then others; excludes sold out & in-cart items
  const relatedItems = useMemo(() => {
    if (cartArray.length === 0) return [];
    const cartCats = new Set(cartArray.map((c) => c.item.category_id));
    const inCart = new Set(cartArray.map((c) => c.item.id));
    const related = items.filter((i) => !inCart.has(i.id) && i.available !== false && cartCats.has(i.category_id));
    const others = items.filter((i) => !inCart.has(i.id) && i.available !== false && !cartCats.has(i.category_id));
    return [...related, ...others].slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, cart]);
  const subtotal = cartArray.reduce((s, c) => s + c.qty * c.item.price, 0);

  // Mirror of backend `_calc_discount` + min-order check for live preview
  const voucherPreview = useMemo(() => {
    if (!appliedVoucher) return { amount: 0, shortage: 0 };
    const minOrder = appliedVoucher.min_order_amount || 0;
    if (subtotal < minOrder) return { amount: 0, shortage: minOrder - subtotal };
    let amount = appliedVoucher.type === "fixed"
      ? appliedVoucher.value
      : (subtotal * appliedVoucher.value) / 100;
    if (appliedVoucher.type === "percent" && (appliedVoucher.max_discount_amount || 0) > 0) {
      amount = Math.min(amount, appliedVoucher.max_discount_amount);
    }
    amount = Math.min(Math.round(amount), subtotal);
    return { amount, shortage: 0 };
  }, [appliedVoucher, subtotal]);
  const discountAmount = voucherPreview.amount;
  const voucherShortage = voucherPreview.shortage;
  const discountedSubtotal = subtotal - discountAmount;
  const taxAmount = Math.round(discountedSubtotal * (outlet?.tax_percent || 0) / 100);
  const serviceAmount = Math.round(discountedSubtotal * (outlet?.service_percent || 0) / 100);
  const grandTotal = discountedSubtotal + taxAmount + serviceAmount;

  // Drop voucher automatically once the cart is emptied
  useEffect(() => {
    if (cartArray.length === 0 && appliedVoucher) {
      setAppliedVoucher(null);
      setVoucherInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartArray.length]);

  const applyVoucher = async () => {
    const code = voucherInput.trim();
    if (!code) return toast.error("Masukkan kode voucher dulu");
    if (cartArray.length === 0) return toast.error("Keranjang masih kosong");
    if (!outlet) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/vouchers/redeem", { outlet_slug: outlet.slug, code, subtotal });
      if (data.valid) {
        setAppliedVoucher(data);
        setVoucherInput(data.code);
        toast.success(data.message);
      } else {
        setAppliedVoucher(null);
        toast.error(data.message || "Voucher tidak valid");
      }
    } catch {
      toast.error("Gagal memvalidasi voucher");
    } finally { setRedeeming(false); }
  };
  const clearVoucher = () => { setAppliedVoucher(null); setVoucherInput(""); };

  // Nomor kamar hanya boleh angka (maks. 6 digit) — backend menyimpannya sebagai integer.
  const updateRoomNumber = (value) => {
    setRoomNumber(String(value).replace(/\D/g, "").slice(0, 6));
    setRoomNumberError("");
  };

  // Ingat nomor kamar terakhir per outlet (mis. setelah tamu refresh halaman menu).
  useEffect(() => {
    if (orderType !== "room" || !outletSlug || roomNumberFromUrl) return;
    const saved = localStorage.getItem(`room_number_${outletSlug}`) || "";
    if (/^\d+$/.test(saved) && Number(saved) > 0) setRoomNumber(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, outletSlug]);

  useEffect(() => {
    if (orderType !== "room" || !outletSlug) return;
    if (/^\d+$/.test(roomNumber) && Number(roomNumber) > 0) {
      localStorage.setItem(`room_number_${outletSlug}`, roomNumber);
    }
  }, [roomNumber, orderType, outletSlug]);

  const placeOrder = async () => {
    if (!outlet) return toast.error("Outlet tidak dikenali");
    if (isOutletClosed(outlet)) {
      return toast.error(outlet.closed_message || "Menu sedang tutup. Silakan kembali lagi nanti.");
    }
    if (cartArray.length === 0) return toast.error("Keranjang kosong");
    if (orderType === "room") {
      const room = roomNumber.trim();
      if (!room) {
        setRoomNumberError("Nomor kamar wajib diisi");
        return toast.error("Nomor kamar wajib diisi");
      }
      if (!/^\d+$/.test(room) || Number(room) <= 0) {
        setRoomNumberError("Nomor kamar harus berupa angka");
        return toast.error("Nomor kamar harus berupa angka");
      }
    }
    const targetNumber = orderType === "room" ? roomNumber.trim() : tableNumberParam;
    if (!targetNumber) return toast.error("Nomor meja tidak terdeteksi");
    setRoomNumberError("");
    const phone = customerPhone.trim();
    if (phone && !/^\+?[0-9]{8,15}$/.test(phone)) {
      const message = "Nomor telepon harus 8–15 digit, boleh diawali +, tanpa spasi atau tanda hubung";
      setCustomerPhoneError(message);
      return toast.error(message);
    }
    setCustomerPhoneError("");
    setPlacing(true);
    try {
      const payload = {
        outlet_slug: outlet.slug,
        table_number: Number(targetNumber),
        order_type: orderType,
        customer_name: customerName,
        customer_phone: phone,
        notes: orderNotes,
        voucher_code: appliedVoucher?.code || "",
        payment_method: paymentMethod,
        items: cartArray.map((c) => ({
          menu_item_id: c.item.id, name: c.item.name, price: c.item.price,
          quantity: c.qty, notes: c.notes || "",
        })),
      };
      const { data } = await api.post("/orders", payload);
      setCart({}); setShowCart(false);
      navigate(`/order/status/${data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal membuat pesanan"));
      if (e?.response?.status === 400 && appliedVoucher) clearVoucher();
    } finally { setPlacing(false); }
  };

   const closeOutletPopup = () => {
     if (outlet?.slug) {
       const popupVersion = `${outlet.updated_at || ""}:${(outlet.popup_message || "").trim()}`;
       localStorage.setItem(`resto_popup_seen_${outlet.slug}_${popupVersion}`, "1");
     }
     setShowOutletPopup(false);
   };

   const closePromotionPopup = () => {
     if (outlet?.slug) {
       const promotionImage = (outlet.promotion_image_url || outlet.promotion_image_file_id) ? (
         outlet.promotion_image_file_id ? fileUrl(outlet.promotion_image_file_id) : outlet.promotion_image_url
       ) : "";
       const promotionVersion = `${outlet.updated_at || ""}:${promotionImage}`;
       localStorage.setItem(`resto_promotion_seen_${outlet.slug}_${promotionVersion}`, "1");
     }
     setShowPromotionPopup(false);
   };

  if (outletError) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <div className="max-w-sm bg-white border border-border rounded-2xl p-8 text-center shadow-warm">
          <div className="w-14 h-14 rounded-full bg-warning/15 grid place-items-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-warning"/>
          </div>
          <h1 className="font-display text-xl font-bold">Outlet Tidak Ditemukan</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="outlet-error-msg">{outletError}</p>
          <Button className="mt-5 rounded-full" onClick={() => navigate("/")}>Kembali ke Beranda</Button>
        </div>
      </div>
    );
  }
  if (!outlet) return <PageState kind="loading" title="Menyiapkan menu" description="Memuat informasi outlet…" className="min-h-screen" />;
  if (isOutletClosed(outlet)) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <div className="max-w-md w-full bg-white border border-border rounded-2xl p-8 text-center shadow-warm">
          <div className="w-14 h-14 rounded-full bg-warning/15 grid place-items-center mx-auto mb-4">
            <Clock3 className="w-6 h-6 text-warning" />
          </div>
          <h1 className="font-display text-2xl font-bold">{outlet.name}</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="menu-closed-msg">
            {outlet.closed_message || "Menu sedang tutup. Silakan kembali lagi nanti."}
          </p>
          {outlet.open_reason === "outside_hours" && outlet.opens_at && (
            <p className="text-sm font-medium text-primary mt-3" data-testid="menu-opens-at">
              Buka kembali pukul {outlet.opens_at}
            </p>
          )}
          {!!outlet.hours && (
            <p className="text-xs text-muted-foreground mt-3">Jam operasional: {outlet.hours}</p>
          )}
          <p className="text-[11px] text-muted-foreground mt-4">
            Halaman ini otomatis diperbarui saat outlet kembali buka.
          </p>
          <Button className="mt-4 rounded-full" variant="outline" onClick={() => navigate("/")}>
            Kembali ke Beranda
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-shell">
      <div className="customer-canvas">

        <StorefrontHeader outlet={outlet} orderType={orderType} tableNumber={tableNumberParam} roomNumber={roomNumber} onSearch={focusSearch} />
        <StorefrontHero outlet={outlet} image={headerSrc} orderType={orderType} mode={mode} roomNumber={roomNumber} tableNumber={tableNumberParam} />

        <div className="px-4 pt-4">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8a726a]" aria-hidden="true" />
            <Input ref={searchRef} data-testid="menu-search-input" placeholder="Cari hidangan autentik, minuman segar..." aria-label="Cari menu" value={search} onChange={(event) => setSearch(event.target.value)} className="storefront-search h-12 rounded-2xl border-transparent bg-accent pl-10 pr-11 text-[13px] placeholder:text-[#7a625a] focus-visible:bg-card" />
            {search && <button type="button" onClick={() => { setSearch(""); searchRef.current?.focus(); }} aria-label="Hapus pencarian" className="absolute right-1 top-1 grid h-10 w-10 place-items-center rounded-full text-muted-foreground"><X className="h-4 w-4" /></button>}
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-hide py-0.5" role="group" aria-label="Pilihan sajian">
            <button type="button" onClick={() => { setActiveCat("all"); setAvailableOnly(false); }} aria-pressed={!availableOnly && activeCat === "all"} className="storefront-chip"><Star className="h-3.5 w-3.5" />Semua Sajian</button>
            <button type="button" onClick={() => setAvailableOnly((value) => !value)} aria-pressed={availableOnly} data-testid="available-filter-btn" className="storefront-chip"><UtensilsCrossed className="h-3.5 w-3.5" />Tersedia</button>
            <button type="button" onClick={() => setShowService(true)} className="storefront-chip"><BellRing className="h-3.5 w-3.5" />Layanan Outlet</button>
          </div>
        </div>

        <div className="menu-rail">
          <div className="flex gap-5 overflow-x-auto scrollbar-hide px-4" role="group" aria-label="Kategori menu">
            <button onClick={() => setActiveCat("all")} data-testid="cat-all-btn" aria-pressed={activeCat === "all"} className="menu-category">Semua</button>
            {categories.map((category) => <button key={category.id} data-testid={`cat-${category.id}-btn`} onClick={() => setActiveCat(category.id)} aria-pressed={activeCat === category.id} className="menu-category">{category.name}</button>)}
          </div>
        </div>

        <main ref={menuRef} tabIndex={-1} id="menu-list" className="storefront-menu px-4 pt-4" aria-busy={menuLoading}>
          {showFeatured && <section ref={featuredRef} className="storefront-featured mb-7" aria-label="Pilihan sajian" data-testid="featured-menu-section">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="flex items-center gap-2 font-display text-[22px] font-medium"><span className="h-2 w-2 shrink-0 rounded-full bg-[#7e5713]" />{featuredCategory ? `Koleksi ${featuredCategory.name}` : "Pilihan Sajian"}</h2>
              <span className="shrink-0 text-[11px] font-semibold tracking-wide text-heritage">Jelajahi Rasa</span>
            </div>
            <div className="-mx-4 flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-2">
              {featuredItems.map((item) => <FeaturedMenuCard key={item.id} item={item} onSelect={() => setSelectedItem(item)} onAdd={() => addToCart(item, 1)} />)}
            </div>
          </section>}
          {!menuLoading && !menuError && filteredItems.length > 0 && <div className="mb-3 flex items-baseline justify-between gap-3"><h2 className="font-display text-[22px] font-medium">Daftar Sajian Utama</h2><span className="text-[11px] font-semibold text-muted-foreground">{filteredItems.filter((item) => item.available !== false).length} Pilihan Tersedia</span></div>}
          {menuLoading ? <PageState kind="loading" title="Memuat sajian" description="Menu Anda sedang disiapkan…" /> : menuError ? (
            <PageState kind="error" title="Menu belum tersedia" description={menuError} onRetry={() => fetchMenu(outlet.id)} />
          ) : filteredItems.length === 0 ? (
            <div className="text-center">
              <PageState title={items.length === 0 ? "Belum ada menu" : search ? "Sajian tidak ditemukan" : availableOnly ? "Tidak ada sajian tersedia" : "Belum ada sajian di kategori ini"} description={items.length === 0 ? "Menu outlet ini belum tersedia. Silakan kembali lagi nanti." : "Coba kata kunci, kategori, atau pilihan ketersediaan yang berbeda."} />
              {items.length > 0 && <Button variant="outline" className="mb-6 rounded-full" onClick={showAllMenu}>Tampilkan semua sajian</Button>}
            </div>
          ) : null}

          {/* Grouped by category when showing all + no search */}
          {!menuLoading && !menuError && (activeCat === "all" && !search && !availableOnly
            ? categories.map((cat) => {
                const catItems = items
                  .filter((i) => i.category_id === cat.id)
                  .sort((a, b) => (b.available !== false) - (a.available !== false));
                if (!catItems.length) return null;
                return (
                  <section key={cat.id} className="mb-7" aria-label={cat.name}>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{cat.name}</h3>
                      <span className="text-[11px] text-muted-foreground">{catItems.length} sajian</span>
                    </div>
                    <div className="space-y-3">
                      {catItems.map((item) => (
                        <MenuCard
                          key={item.id}
                          item={item}
                          onSelect={() => setSelectedItem(item)}
                          onAdd={(e) => { e.stopPropagation(); addToCart(item, 1); }}
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            : (
              <div className="space-y-3">
                {filteredItems.map((item) => (
                  <MenuCard
                    key={item.id}
                    item={item}
                    onSelect={() => setSelectedItem(item)}
                    onAdd={(e) => { e.stopPropagation(); addToCart(item, 1); }}
                  />
                ))}
              </div>
            ))
          }
          <div className="storefront-hospitality mt-6">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#ffddb1]/50 text-[#7e5713]"><Soup className="h-6 w-6" /></span>
            <div><h2 className="font-sans text-base font-semibold">Punya Preferensi Khusus?</h2><p className="mt-1 text-[13px] leading-[19px] text-muted-foreground">Sampaikan pantangan bahan atau tingkat kepedasan pada catatan hidangan sebelum memesan.</p></div>
          </div>
        </main>

        {/* ── Item Detail Drawer ── */}
        <AnimatePresence>
          {selectedItem && (
            <ItemDrawer item={selectedItem} settings={outlet} categoryName={categories.find((category) => category.id === selectedItem.category_id)?.name}
              tableNumber={orderType === "room" ? roomNumber : tableNumberParam} orderType={orderType} onClose={() => setSelectedItem(null)}
              onAdd={(qty, notes) => { addToCart(selectedItem, qty, notes); setSelectedItem(null); }} />
          )}
        </AnimatePresence>

        {/* ── Cart Drawer ── */}
        <AnimatePresence>
          {showCart && (
            <CartDrawer cartArray={cartArray} subtotal={subtotal} taxAmount={taxAmount} serviceAmount={serviceAmount}
              grandTotal={grandTotal} discountAmount={discountAmount} voucherShortage={voucherShortage}
              voucherInput={voucherInput} setVoucherInput={setVoucherInput} appliedVoucher={appliedVoucher}
              onApplyVoucher={applyVoucher} onClearVoucher={clearVoucher} redeeming={redeeming}
              relatedItems={relatedItems}
              tableNumber={orderType === "room" ? roomNumber : tableNumberParam} orderType={orderType} customerName={customerName} setCustomerName={setCustomerName}
              customerPhone={customerPhone} customerPhoneError={customerPhoneError}
              onCustomerPhoneChange={(value) => { setCustomerPhone(value); setCustomerPhoneError(""); }}
              roomNumber={roomNumber} onRoomNumberChange={updateRoomNumber} roomNumberError={roomNumberError}
              orderNotes={orderNotes} setOrderNotes={setOrderNotes} onClose={() => setShowCart(false)}
              onAdd={addToCart} onRemove={removeFromCart} onPlace={placeOrder} placing={placing}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              settings={outlet} />
          )}
        </AnimatePresence>

        {/* ── Floating Cart Button ── */}
        {cartCount > 0 && (
          <motion.button initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} onClick={() => setShowCart(true)} data-testid="view-cart-btn" className={`cart-bar ${showCart ? "invisible" : ""}`} aria-label={`Lihat pesanan, ${cartCount} item, total ${formatIDR(grandTotal)}`}>
            <span className="flex min-w-0 items-center gap-3">
              <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-heritage">
                <ShoppingBag className="h-5 w-5" /><span className="cart-count">{cartCount}</span>
              </span>
              <span className="min-w-0 text-left"><span className="block text-[10px] font-semibold uppercase tracking-wider text-white/80">Total Pesanan</span><span className="mt-1 block text-lg font-bold tabular-nums">{formatIDR(grandTotal)}</span></span>
            </span>
            <span className="cart-action"><span>Lihat<br className="sm:hidden" /> Pesanan</span><ArrowRight className="h-4 w-4 shrink-0" /></span>
          </motion.button>
        )}

        <nav className="storefront-nav" aria-label="Navigasi pemesanan">
          <button type="button" onClick={showAllMenu} className="text-heritage" aria-current="page" data-testid="nav-menu-btn"><UtensilsCrossed /><span>Menu</span></button>
          <button type="button" onClick={showFeaturedMenu} data-testid="nav-featured-btn"><Star /><span>{featuredCategory ? "Keraton" : "Pilihan"}</span></button>
          <button type="button" onClick={() => setShowCart(true)} data-testid="nav-orders-btn" className="relative"><ReceiptText />{cartCount > 0 && <span className="storefront-nav-dot" aria-hidden="true" />}<span>Pesanan</span></button>
          <button type="button" onClick={() => setShowService(true)} data-testid="nav-service-btn"><BellRing /><span>Layanan</span></button>
        </nav>
        {showService && <OutletServiceSheet outlet={outlet} onClose={() => setShowService(false)} />}

         {/* ── Outlet Popup ── */}
         <Dialog open={showOutletPopup} onOpenChange={(open) => { if (!open) closeOutletPopup(); }}>
           <DialogContent className="max-w-sm rounded-2xl">
             <DialogHeader>
               <DialogTitle>{outlet.popup_title || "Informasi Outlet"}</DialogTitle>
               <DialogDescription>{outlet.popup_message || ""}</DialogDescription>
             </DialogHeader>
             <DialogFooter>
               <Button onClick={closeOutletPopup} className="w-full">Mengerti</Button>
             </DialogFooter>
           </DialogContent>
         </Dialog>

         {/* ── Promotion Popup ── */}
         <Dialog open={showPromotionPopup} onOpenChange={(open) => { if (!open) closePromotionPopup(); }}>
           <DialogContent className="max-w-sm rounded-2xl p-0 overflow-hidden">
             <DialogTitle className="sr-only">{outlet.promotion_title || `Promosi ${outlet.name}`}</DialogTitle>
             <DialogDescription className="sr-only">{outlet.promotion_description || "Informasi promosi outlet."}</DialogDescription>
             <div className="relative">
               {(() => {
                 const promotionImage = outlet.promotion_image_file_id
                   ? fileUrl(outlet.promotion_image_file_id)
                   : outlet.promotion_image_url;
                 return promotionImage ? (
                   <img src={promotionImage} alt="Promosi" className="w-full h-auto object-cover" />
                 ) : (
                   <div className="w-full h-64 bg-gradient-to-br from-primary/50 to-primary/10 flex items-center justify-center">
                     <span className="text-4xl">📢</span>
                   </div>
                 );
               })()}
             </div>
             {(outlet.promotion_title || outlet.promotion_description) && (
               <div className="p-5">
                 {outlet.promotion_title && (
                   <h3 className="font-display text-lg font-bold text-gray-900 mb-2">
                     {outlet.promotion_title}
                   </h3>
                 )}
                 {outlet.promotion_description && (
                   <p className="text-sm text-gray-600 leading-relaxed mb-4">
                     {outlet.promotion_description}
                   </p>
                 )}
                 <Button onClick={closePromotionPopup} className="w-full">
                   Tutup
                 </Button>
               </div>
             )}
           </DialogContent>
         </Dialog>
      </div>
    </div>
  );
}

// Outlet dianggap tutup bila backend menyatakan `is_open_now === false` — mencakup
// tutup manual maupun di luar jam buka. Fallback `menu_open` untuk payload lama.
function isOutletClosed(outlet) {
  if (!outlet) return false;
  if (outlet.is_open_now !== undefined && outlet.is_open_now !== null) return outlet.is_open_now === false;
  return outlet.menu_open === false;
}

function resolveCategoryParam(categories, param) {
  if (!param || !categories?.length) return "";
  const exactId = categories.find((c) => c.id === param);
  if (exactId) return exactId.id;
  const byName = categories.find((c) => (c.name || "").toLowerCase() === String(param).toLowerCase());
  if (byName) return byName.id;
  if (/^\d+$/.test(param)) {
    const idx = Number(param) - 1;
    if (idx >= 0 && idx < categories.length) return categories[idx].id;
  }
  return "";
}
